// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
// API module for uploading contact data via CSV to the Hardfin system.
// Provides type-safe upload with Zod validation for the import response.
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { contactSchema } from '@/contact/api/types';
import { z } from 'zod';

const validationErrorFieldSchema = z.object({
  fieldName: z.string(),
  error: z.string(),
  errorCode: z.number(),
});

const processedContactDataSchema = z.object({
  contactName: z.string(),
  index: z.number(),
  created: z.boolean(),
  updated: z.boolean(),
  noOp: z.boolean(),
  validatedContact: contactSchema.nullable(),
  fieldsUpdated: z.record(z.boolean()),
  errors: z.array(validationErrorFieldSchema),
});

const rowErrorDataSchema = z.object({
  index: z.number(),
  errors: z.array(validationErrorFieldSchema),
});

export const uploadContactDataResponseSchema = z.object({
  data: z.object({
    success: z.boolean(),
    items: z.array(processedContactDataSchema),
    structureErrors: z.array(validationErrorFieldSchema),
    rowErrors: z.array(rowErrorDataSchema),
  }),
});

export type ContactDataUploadResult = z.infer<
  typeof uploadContactDataResponseSchema
>['data'];

/**
 * Uploads a CSV file containing contact data for import.
 *
 * @param formData - FormData containing the CSV file under the "data" key
 * @returns Promise resolving to the upload result with processed contacts and any errors
 * @throws Will re-throw any parsing error from the response validation
 */
export async function uploadContactData(
  formData: FormData
): Promise<ContactDataUploadResult> {
  const url = '/v1/import/contact-data';
  const response = await api.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  const parsed = await parseResponse(
    uploadContactDataResponseSchema,
    response.data,
    {
      schemaName: 'UploadContactData',
      endpoint: url,
    }
  );
  return parsed.data;
}
